import cn from 'classnames'
import { isValidElement, memo, ReactElement, useCallback, useMemo } from 'react'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'

import { isItem } from '../../helpers'
import { TabsProps, THeaderItemRenderer } from '../../models'
import { ItemProps } from '../item/Item'
import { HeaderSharedBlockProps } from '../shared'

import styles from './Header.module.scss'

export interface HeaderProps extends Pick<TabsProps, 'classes'> {
  items: React.ReactElement<ItemProps, string | React.JSXElementConstructor<ItemProps>>[]
  sharedBlock: React.ReactElement<
    HeaderSharedBlockProps,
    string | React.JSXElementConstructor<HeaderSharedBlockProps>
  >[]
  activeIndex: number
  onItemClick: (index: number) => void
  renderCustomHeaderItem?: THeaderItemRenderer
}

export const Header = memo<HeaderProps>(
  ({ items, sharedBlock, classes, activeIndex, onItemClick, renderCustomHeaderItem }) => {
    const renderDefaultHeaderItem = useCallback<THeaderItemRenderer>(
      ({ item, index, isActive, onItemClick }) => {
        const preparedTitle =
          typeof item.props.title !== 'string' ? (
            item.props.title
          ) : (
            <Translation text={item.props.title} span />
          )

        return (
          <Button
            key={index}
            active={isActive}
            disabled={item.props.isDisabled}
            className={cn(
              styles.item,
              classes?.item,
              classes?.active && {
                [classes?.active]: isActive,
              }
            )}
            onClick={() => onItemClick(index)}
            variant={'text'}
          >
            {preparedTitle}
          </Button>
        )
      },
      [classes?.item, classes?.active]
    )

    const leftSharedBlock = useMemo(() => {
      return sharedBlock.filter((block) => {
        if (isValidElement(block)) {
          return block.props.position === 'left'
        }
        return false
      })
    }, [sharedBlock])

    const rightSharedBlock = useMemo(() => {
      return sharedBlock.filter((block) => {
        if (isValidElement(block)) {
          return block.props.position !== 'left'
        }
        return false
      })
    }, [sharedBlock])

    const mapItem = useCallback(
      (item: ReactElement, index: number) => {
        if (isItem(item)) {
          const isActive = index === activeIndex
          const args = { item, index, isActive, onItemClick }

          if (renderCustomHeaderItem) {
            return renderCustomHeaderItem(args)
          }

          return renderDefaultHeaderItem(args)
        }

        return null
      },
      [activeIndex, onItemClick, renderCustomHeaderItem, renderDefaultHeaderItem]
    )

    const renderHeader = useCallback(() => {
      return items.map(mapItem)
    }, [items, mapItem])

    return (
      <div className={cn(styles.header, classes?.header)}>
        <RenderWhen condition={leftSharedBlock.length > 0}>{leftSharedBlock}</RenderWhen>
        <div className={cn(styles.items, classes?.items)}>{renderHeader()}</div>
        <RenderWhen condition={rightSharedBlock.length > 0}>{rightSharedBlock}</RenderWhen>
      </div>
    )
  }
)

Header.displayName = 'Header'
